import Modal from "@/components/common/modal";
import { DialogFooter } from "@/components/ui/dialog";
import type { ISignDocument } from "@/queries/process/signature.api";

import {
  Avatar,
  AvatarFallback,
  Badge,
  Body,
  Button,
} from "@elaniintc/goes-design-system";
import { groupSignersByPosition } from "@/pages/procedures/components/signature/utils/groupBySignersPosition";
import { useAuth } from "@/contexts/auth-provider/useAuth";
import type { Signer } from "./type";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  documentSignedSelected?: ISignDocument;
}

type SingType = "simple" | "certified" | "automatic";

function formatFecha(fechaISO?: string | null): string {
  if (!fechaISO) return "";

  const date = new Date(fechaISO);

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();

  const minutes = String(date.getMinutes()).padStart(2, "0");
  let hours = date.getHours();

  hours = hours % 12 || 12;

  return `${day}/${month}/${year} a las ${hours}:${minutes}`;
}

const SignatureDetailModal = ({
  isOpen,
  onClose,
  documentSignedSelected,
}: Props) => {
  const { authUser } = useAuth();

  const signerLot = groupSignersByPosition(
    documentSignedSelected?.signers.data as unknown as Signer[],
  ).find(
    (slot) =>
      slot.backup?.email === authUser?.email ||
      slot.primary?.email === authUser?.email,
  );

  const signers = [
    documentSignedSelected?.signedBy.data.find(
      (x) => x.id === signerLot?.backup?.id || x.id === signerLot?.primary?.id,
    ),
  ];

  const getLabel = (singType: SingType) => {
    switch (singType) {
      case "certified":
        return "Certificada";
      case "automatic":
        return "Sello Institucional";
      default:
        return "Simple";
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Firma de documento"
      description="Esta es la información de tu firma en este documento."
      modalContentClassName="max-w-[520px] w-full gap-5"
    >
      {signers.map((signer) => (
        <div key={signer?.id} className="flex flex-col gap-6">
          <div className="flex flex-col gap-1">
            <Body size="sm" variant="bold" color="loud">
              Firmante
            </Body>
            <Badge
              key={signer?.id}
              className="py-6 px-2 gap-2"
              variant="neutral"
            >
              <div>
                <Avatar size="xs" color="action" data-testid="signature-avatar">
                  <AvatarFallback>{signer?.name.charAt(0)}</AvatarFallback>
                </Avatar>
              </div>
              <div className="flex flex-col">
                <Body size="sm" variant="bold">
                  {signer?.name} {signer?.last_name}
                </Body>
                <Body size="xs" color="quiet">
                  {signer?.email}
                </Body>
              </div>
            </Badge>
          </div>

          <div className="flex flex-col gap-2">
            <Body size="sm" variant="bold" color="loud">
              Información
            </Body>
            <Body size="xs" color="quiet">
              Firma{" "}
              {getLabel(
                documentSignedSelected?.sign_type as SingType,
              ).toLocaleLowerCase()}{" "}
              efectuada el:{" "}
              {formatFecha(documentSignedSelected?.last_signed_at)}
            </Body>
            <Body size="xs" color="quiet">
              IP: {signer?.pivot.signer_ip}
            </Body>
          </div>
        </div>
      ))}

      <DialogFooter className="pt-1">
        <div className="w-full">
          <Button
            size="lg"
            variant="tertiary"
            className="w-full rounded-lg"
            type="submit"
            onClick={onClose}
          >
            Aceptar
          </Button>
        </div>
      </DialogFooter>
    </Modal>
  );
};

export default SignatureDetailModal;
